import { IGameState } from '../../State/Game/IGameState';
import AudioClip, { AudioTrackType } from '../Audio/AudioClip';
import System from '../System';
import { Howler } from 'howler';

export enum AudioSystemEvent {
    PLAY = 'audioSystemEventPlay',
    STOP = 'audioSystemEventStop',
}

export default class AudioSystem extends System {
    public audioClips: AudioClip[];

    public audioEnabled: boolean = true;
    public windowHasFocus: boolean = true;
    public SFXVolume = 1;
    public MusicVolume = 1;

    public init() {
        this.audioClips = [];

        this.scene.on(AudioSystemEvent.PLAY, (clip: AudioClip) => {
            this.playAudio(clip);
        });
        this.scene.on(AudioSystemEvent.STOP, (clip: AudioClip) => {
            this.destroyAudioClip(clip);
        });

        const targets = [window, document];
        targets.forEach(target => {
            if (!target) return;
            target.addEventListener('pagehide', this.checkFocus.bind(this));
            target.addEventListener('pageshow', this.checkFocus.bind(this));
            target.addEventListener('blur', this.checkFocus.bind(this));
            target.addEventListener('focus', this.checkFocus.bind(this));
            target.addEventListener(
                'visibilitychange',
                this.checkFocus.bind(this)
            );
            target.onfocus = this.checkFocus.bind(this);
            target.onblur = this.checkFocus.bind(this);
        });
        this.checkFocus();
    }

    public handleStateChanged(state: IGameState): void {
        this.audioEnabled = state.context.audioEnabled;
        this.SFXVolume = state.playerState.volumeSFX;
        this.MusicVolume = state.playerState.volumeMusic;

        this.handleAudio();
    }

    private isMuted() {
        return this.audioEnabled === false || this.windowHasFocus === false;
    }

    private handleAudio() {
        Howler.mute(this.isMuted());

        this.audioClips.forEach(clip => {
            clip.volume = this.getClipVolume(clip);
        });
    }

    private getClipVolume(clip: AudioClip): number {
        switch (clip.track) {
            case AudioTrackType.SFX:
                return this.SFXVolume;
            case AudioTrackType.MUSIC:
                return this.MusicVolume;
        }

        return 0;
    }

    private checkFocus() {
        this.windowHasFocus =
            document.visibilityState === 'visible' || document.hasFocus();
        this.handleAudio();
    }

    public onDestroy(): void {
        this.audioClips.slice(0).forEach(this.destroyAudioClip.bind(this));
        this.audioClips = [];

        this.scene.off(AudioSystemEvent.PLAY);
        this.scene.off(AudioSystemEvent.STOP);
    }

    public playAudio(clip: AudioClip) {
        this.audioClips.push(clip);
        clip.onComplete(this.destroyAudioClip.bind(this));
        clip.volume = this.getClipVolume(clip);
        clip.play();
    }

    private destroyAudioClip(clip: AudioClip) {
        const index = this.audioClips.indexOf(clip);
        if (index !== -1) {
            this.audioClips.splice(index, 1);
        }

        clip.destroy();
    }

    protected onLateUpdate(): void {}

    protected onStart(): void {}

    protected onStop(): void {}

    protected onUpdate(): void {}

    async preload(): Promise<any> {
        return undefined;
    }
}
